import { memo } from 'react';

import { currencyCodeFor } from '../../format/money';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps, isBlankNumber } from './shared';

export type MoneyProps = EmptyProps & {
  /** The amount. `0` renders as a zero amount, not as empty. */
  readonly value: number | null | undefined;
  /**
   * ISO 4217 code, when the amount carries one from `SUPPORTED_CURRENCY_CODES`.
   *
   * Omit it, or pass a code outside `SUPPORTED_CURRENCY_CODES`, and the
   * component renders `emptyValue`. A present unsupported code is reported once.
   */
  readonly code?: string;
  /**
   * Fractional digits.
   *
   * Omit it. Codes in `SUPPORTED_CURRENCY_CODES` use two decimal places. Forcing a count is for
   * the rare column that has to align against something else.
   */
  readonly digits?: number;
};

/**
 * Renders `emptyValue` for a missing currency, an unsupported currency, a
 * blank amount, or a non-finite amount. `currencyCodeFor` reports a present
 * unsupported code. A blank amount never reaches `format.money`. A non-finite
 * amount does, and is reported once.
 */
const Money = memo(
  ({
    value,
    code,
    digits,
    emptyValue = DEFAULT_EMPTY_VALUE,
  }: MoneyProps): JSX.Element => {
    const format = useFormat();
    const currency = currencyCodeFor(code, 'formatMoney.code');

    if (currency === undefined || isBlankNumber(value)) {
      return <>{emptyValue}</>;
    }

    const amount = format.money(value, { code: currency, digits });
    return <>{amount === '' ? emptyValue : amount}</>;
  }
);

Money.displayName = 'Money';

export default Money;
